import React, { useState } from 'react'

import { Info, ArrowForward } from '@mui/icons-material'
import {
  Box,
  Chip,
  Typography,
  Popover,
  IconButton,
  Paper
} from '@mui/material'

type AppointmentStatusInfo = {
  status: string
  color: 'success' | 'primary' | 'error' | 'default'
  description: string
  isEndState?: boolean
}

// PSC Appointment workflow
const appointmentFlowStatuses: AppointmentStatusInfo[] = [
  {
    status: 'NONE',
    color: 'default',
    description: 'No appointment scheduled'
  },
  {
    status: 'SCHEDULED',
    color: 'success',
    description: 'Appointment booked'
  },
  {
    status: 'CONFIRMED',
    color: 'success',
    description: 'Appointment confirmed'
  },
  {
    status: 'COMPLETED',
    color: 'primary',
    description: 'Appointment completed',
    isEndState: true
  }
]

const appointmentAlternativeStatuses: AppointmentStatusInfo[] = [
  {
    status: 'CANCELLED',
    color: 'error',
    description: 'Appointment cancelled',
    isEndState: true
  }
]

const StatusFlow = ({
  statuses,
  title,
  showArrows = true
}: {
  statuses: AppointmentStatusInfo[]
  title: string
  showArrows?: boolean
}) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
      {title}
    </Typography>
    <Box sx={{
      p: 1,
      bgcolor: 'grey.50',
      borderRadius: 1,
      border: '1px solid',
      borderColor: 'divider'
    }}>
      {showArrows ? (
        // Linear flow with arrows between chips
        <Box sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 0.8,
          flexWrap: 'wrap'
        }}>
          {statuses.map((info, index) => (
            <Box key={info.status} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.8 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.3 }}>
                <Chip
                  label={info.status}
                  color={info.color}
                  size="small"
                  variant="outlined"
                  sx={{
                    minWidth: '70px',
                    fontSize: '0.65rem',
                    height: '20px',
                    fontWeight: 500,
                    ...(info.isEndState && {
                      borderWidth: 2,
                      fontWeight: 600
                    })
                  }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontSize: '0.6rem',
                    textAlign: 'center',
                    maxWidth: '80px',
                    lineHeight: 1.1,
                    minHeight: '18px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  {info.description}
                </Typography>
              </Box>

              {/* Arrow centered at chip level */}
              {index < statuses.length - 1 && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  height: '20px', // Match small chip height
                  mt: 0
                }}>
                  <ArrowForward
                    sx={{
                      color: 'text.secondary',
                      fontSize: 10
                    }}
                  />
                </Box>
              )}
            </Box>
          ))}
        </Box>
      ) : (
        // Simple list without arrows
        <Box sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          flexWrap: 'wrap'
        }}>
          {statuses.map((info) => (
            <Box key={info.status} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.3 }}>
              <Chip
                label={info.status}
                color={info.color}
                size="small"
                variant="outlined"
                sx={{
                  minWidth: '70px',
                  fontSize: '0.65rem',
                  height: '20px',
                  fontWeight: 500,
                  ...(info.isEndState && {
                    borderWidth: 2,
                    fontWeight: 600
                  })
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontSize: '0.6rem',
                  textAlign: 'center',
                  maxWidth: '80px',
                  lineHeight: 1.1,
                  minHeight: '18px',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                {info.description}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  </Box>
)

export default function PSCAppointmentHeaderInfo() {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation() // Prevent column sorting
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)

  return (
    <>
      <IconButton
        aria-label="PSC appointment status info"
        onClick={handleClick}
        size="small"
        sx={{
          color: 'text.secondary',
          ml: 0.5,
          p: 0.3,
          '&:hover': {
            color: 'primary.main',
            bgcolor: 'primary.50'
          }
        }}
      >
        <Info sx={{ fontSize: 16 }} />
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            maxWidth: 400,
            maxHeight: 300,
            overflow: 'auto'
          }
        }}
      >
        <Paper sx={{ p: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
            PSC Appointment Guide
          </Typography>

          <StatusFlow
            statuses={appointmentFlowStatuses}
            title="Normal Appointment Flow"
            showArrows={true}
          />

          <StatusFlow
            statuses={appointmentAlternativeStatuses}
            title="Alternative State"
            showArrows={false}
          />

          <Box sx={{ mt: 1.5, p: 1, bgcolor: 'info.50', borderRadius: 1 }}>
            <Typography variant="caption" color="info.main" fontWeight={600} sx={{ fontSize: '0.65rem' }}>
              💡 Note:
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1, fontSize: '0.65rem' }}>
              PSC appointments are only for Quest lab orders. They allow patients to visit Quest locations for sample collection.
            </Typography>
          </Box>
        </Paper>
      </Popover>
    </>
  )
}